import React, { useEffect, useState } from 'react'
import { useQueryParams } from '@fs/zion-router'
import { Separator, useOverlay } from '@fs/zion-ui'
import { useTranslation } from 'react-i18next'
import { useUser } from '@fs/zion-user'
import { useSlsStateContext } from '@fs/zion-sls'
import ShowChangeHistory from '../ui/ShowChangeHistory'
import TranscriptAlertBanner from './components/TranscriptAlertBanner'
import BlankState from '../ui/BlankState'
import SignInBlankState from '../ui/SignInBlankState'
import AddNamesPanel from '../../../fullText/components/addNamesPanel/AddNamesPanel'
import HouseholdList from '../../../../components/generic/householdList/HouseholdList'
import useHouseholdList from '../../../../components/generic/householdList/hooks/useHouseholdsList'

/**
 * Main panel for the transcript and names indexing interface. Displays persons grouped by household,
 * provides filtering, transcript banner, and manages the full text overlay.
 *
 * Features:
 * - Household list with filtering by person name
 * - Transcript alert banner when transcripts are available
 * - Full text overlay controlled by query params
 * - New group card at the end of the list for starting a group
 * - Conditional rendering based on sign-in status and blank state
 * - Change history display
 * - Add names panel for creating new persons
 *
 * @component
 * @param {Object} props
 * @param {boolean} [props.loading=false] - External loading state indicator
 * @returns {JSX.Element}
 */
const TranscriptAndNamesPanel = ({ loading = false, additionalHeaderContent }) => {
  const [t] = useTranslation()
  const { signedIn } = useUser()
  const { currentImageArk } = useSlsStateContext()
  const [filterValue, setFilterValue] = useState('')

  // FSS-14999: the panel stays mounted across image navigation, so a leftover filter would
  // silently empty the household list on every other image. Reset it when the image changes.
  const [prevImageArk, setPrevImageArk] = useState(currentImageArk)
  if (currentImageArk !== prevImageArk) {
    setPrevImageArk(currentImageArk)
    setFilterValue('')
  }

  const { query, setQuery } = useQueryParams()
  const { view } = query || {}
  const { householdListProps, showBlankState, showChangeHistory } = useHouseholdList({
    loading,
    filterValue,
  })
  const fullTextOverlay = useOverlay({ initiallyOpen: view === 'fullText' })

  const handleViewTranscript = () => setQuery({ view: 'fullText' })

  // The single source of truth here is the query param
  useEffect(() => {
    if (view === 'fullText') {
      fullTextOverlay.open()
    } else {
      fullTextOverlay.close()
    }
  }, [view, fullTextOverlay])

  // "Select", not "Click": this panel is used on touch.
  const description = t(
    'transcript-and-names-panel.persons-list-description',
    'Select a person to edit their details, manage their group, or attach this source to Family Tree.'
  )

  return (
    <>
      {signedIn ? (
        <>
          {/* Transcript Alert Banner */}
          <TranscriptAlertBanner handleViewTranscript={handleViewTranscript} fullTextOverlay={fullTextOverlay} />

          {additionalHeaderContent && (
            <>
              <Separator size="xs" />
              {additionalHeaderContent}
            </>
          )}

          {showBlankState ? (
            <BlankState />
          ) : (
            <HouseholdList
              description={description}
              filterValue={filterValue}
              setFilterValue={setFilterValue}
              {...householdListProps}
            />
          )}
        </>
      ) : (
        <>
          {showBlankState ? (
            <SignInBlankState />
          ) : (
            <HouseholdList filterValue={filterValue} setFilterValue={setFilterValue} {...householdListProps} />
          )}
        </>
      )}

      {/* Change History */}
      {showChangeHistory && <ShowChangeHistory showDivider={false} />}

      <AddNamesPanel />
    </>
  )
}

export default TranscriptAndNamesPanel
